import { logError } from '@/utils/logger';
import SearchableSelect from '@/components/SearchableSelect';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, MapPin, Funnel, MagnifyingGlass, FilePdf, X, Trash, FolderOpen, Signature, ArrowsLeftRight, IdentificationCard } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import ExportButton from '@/components/ExportButton';
import PhotoUpload from '@/components/PhotoUpload';
import SignaturePad from '@/components/SignaturePad';
import AgentDossier from '@/components/AgentDossier';
import ContactActions from '@/components/ContactActions';
import ImageLightbox from '@/components/ImageLightbox';
import { SortableHeader, useSort } from '@/components/SortableHeader';

const Agents = () => {
  const { t } = useTranslation();
  const [agents, setAgents] = useState([]);
  const [zones, setZones] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [selectedZone, setSelectedZone] = useState('all');
  const [userPermissions, setUserPermissions] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dossierAgent, setDossierAgent] = useState(null);
  const [siteFilter, setSiteFilter] = useState('all');
  const [transferAgent, setTransferAgent] = useState(null);
  const [transferSite, setTransferSite] = useState('');
  const [clientsList, setClientsList] = useState([]);
  const [showAgentSigPad, setShowAgentSigPad] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { src, alt, filename }

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clients`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(setClientsList)
      .catch(() => {});
  }, []);

  const handleTransfer = async () => {
    if (!transferSite) { toast.error('Choisissez le nouveau site'); return; }
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/agents/${transferAgent.agent_id}`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assigned_site: transferSite }),
      });
      if (res.ok) {
        toast.success(`${transferAgent.name} transféré vers « ${transferSite} »`);
        setTransferAgent(null);
        setTransferSite('');
        fetchAgents();
      } else { const err = await res.json(); toast.error(err.detail || 'Erreur'); }
    } catch { toast.error('Erreur'); }
  };
  const [newZoneName, setNewZoneName] = useState('');
  const [showNewZoneInput, setShowNewZoneInput] = useState(false);
  const [formData, setFormData] = useState({
    matricule: '', cnss_number: '', inpp_number: '',
    nom: '', post_nom: '', prenom: '',
    email: '', phone: '', address: '',
    etat_civil: '', nombre_enfants: '', date_engagement: '', remuneration: '',
    client_id: 'none', assigned_site: 'none', assignment_zone: '',
    skills: '', availability: 'available', zone_id: '', photo_url: '', signature_url: '',
    id_card_url: '', fonction: '',
  });

  // Define useCallback functions BEFORE useEffect that uses them
  const fetchAgents = useCallback(async () => {
    try {
      const url = selectedZone && selectedZone !== 'all'
        ? `${process.env.REACT_APP_BACKEND_URL}/api/agents?zone_id=${selectedZone}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/agents`;
      
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setAgents(data);
      }
    } catch (error) {
      logError('Failed to fetch agents:', error);
    }
  }, [selectedZone]);

  const fetchInitialData = useCallback(async () => {
    try {
      const [userRes, zonesRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/zones`, { credentials: 'include' }),
      ]);

      if (userRes.ok) {
        const userData = await userRes.json();
        setUserPermissions({...userData.permissions, effective: userData.effective_permissions || {}});
        setUserRole(userData.role);
        if (!userData.permissions.can_view_all_zones && userData.zone_id) {
          setSelectedZone(userData.zone_id);
        }
      }

      if (zonesRes.ok) {
        const zonesData = await zonesRes.json();
        setZones(zonesData);
        if (zonesData.length > 0) {
          setFormData(prev => ({ ...prev, zone_id: prev.zone_id || zonesData[0].zone_id }));
        }
      }

      // Fetch clients for site selection
      const clientsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clients`, { credentials: 'include' });
      if (clientsRes.ok) setClients(await clientsRes.json());
    } catch (error) {
      logError('Failed to fetch initial data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // Fetch agents when zones are loaded or selectedZone changes
  useEffect(() => {
    if (zones.length > 0) {
      fetchAgents();
    }
  }, [zones.length, selectedZone, fetchAgents]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const fullName = [formData.nom, formData.post_nom, formData.prenom].filter(Boolean).join(' ') || 'Agent';
      const payload = {
        ...formData,
        name: fullName,
        matricule: formData.matricule || null,
        cnss_number: formData.cnss_number || null,
        inpp_number: formData.inpp_number || null,
        nombre_enfants: formData.nombre_enfants ? parseInt(formData.nombre_enfants) : null,
        remuneration: formData.remuneration ? parseFloat(formData.remuneration) : null,
        date_engagement: formData.date_engagement || null,
        etat_civil: formData.etat_civil || null,
        client_id: formData.client_id === 'none' ? null : formData.client_id,
        assigned_site: formData.assigned_site === 'none' ? null : formData.assigned_site,
        skills: formData.skills ? formData.skills.split(',').map(s => s.trim()) : [],
        photo_url: formData.photo_url || null, signature_url: formData.signature_url || null,
        id_card_url: formData.id_card_url || null,
        fonction: formData.fonction || null,
      };

      const url = editingAgent
        ? `${process.env.REACT_APP_BACKEND_URL}/api/agents/${editingAgent.agent_id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/agents`;

      const response = await fetch(url, {
        method: editingAgent ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast.success(editingAgent ? 'Agent modifié' : 'Agent ajouté');
        setIsDialogOpen(false);
        resetForm();
        fetchAgents();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      logError('Failed to save agent:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleEdit = (agent) => {
    setEditingAgent(agent);
    setFormData({
      matricule: agent.matricule || '', cnss_number: agent.cnss_number || '', inpp_number: agent.inpp_number || '',
      nom: agent.nom || '', post_nom: agent.post_nom || '', prenom: agent.prenom || '',
      email: agent.email, phone: agent.phone,
      address: agent.address || '',
      etat_civil: agent.etat_civil || '', nombre_enfants: agent.nombre_enfants ?? '',
      date_engagement: agent.date_engagement || '', remuneration: agent.remuneration ?? '',
      client_id: agent.client_id || 'none',
      assigned_site: agent.assigned_site || 'none',
      assignment_zone: agent.assignment_zone || '',
      skills: (agent.skills || []).join(', '), availability: agent.availability,
      zone_id: agent.zone_id || 'zone_kinshasa', photo_url: agent.photo_url || '', signature_url: agent.signature_url || '',
      id_card_url: agent.id_card_url || '', fonction: agent.fonction || '',
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      matricule: '', cnss_number: '', inpp_number: '',
      nom: '', post_nom: '', prenom: '',
      email: '', phone: '', address: '',
      etat_civil: '', nombre_enfants: '', date_engagement: '', remuneration: '',
      client_id: 'none', assigned_site: 'none', assignment_zone: '',
      skills: '', availability: 'available',
      zone_id: zones.length > 0 ? zones[0].zone_id : 'zone_kinshasa',
      photo_url: '', signature_url: '',
      id_card_url: '', fonction: '',
    });
    setEditingAgent(null);
    setNewZoneName('');
    setShowNewZoneInput(false);
  };

  const getAvailabilityBadge = (availability) => {
    const config = {
      available: { label: t('agents.available'), className: 'bg-[#10B981]/20 text-[#10B981] border-[#10B981]/30' },
      assigned: { label: t('agents.assigned'), className: 'bg-[#2563EB]/20 text-[#2563EB] border-[#2563EB]/30' },
      unavailable: { label: t('agents.unavailable'), className: 'bg-[#94A3B8]/20 text-[#94A3B8] border-[#94A3B8]/30' },
    };
    const { label, className } = config[availability] || config.available;
    return <Badge className={`${className} border`}>{label}</Badge>;
  };

  const getZoneName = (zoneId) => {
    const zone = zones.find(z => z.zone_id === zoneId);
    return zone ? zone.name : zoneId;
  };

  const canEdit = userPermissions?.effective?.agents?.write;
  const { sortConfig, onSort, sortData } = useSort('name', 'asc');

  const addNewZone = async () => {
    const name = newZoneName.trim();
    if (!name) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/zones`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      if (res.ok) {
        const newZone = await res.json();
        setZones(prev => [...prev, newZone]);
        setFormData(prev => ({ ...prev, zone_id: newZone.zone_id }));
        setNewZoneName('');
        setShowNewZoneInput(false);
        toast.success(`Zone "${name}" ajoutée`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch { toast.error('Erreur lors de la création de la zone'); }
  };

  const handleDelete = async (agentId) => {
    if (!window.confirm('Supprimer cet agent ?')) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/agents/${agentId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { toast.success('Agent supprimé'); fetchAgents(); }
      else { const err = await res.json(); toast.error(err.detail || 'Erreur'); }
    } catch { toast.error('Erreur'); }
  };

  if (loading) {
    return <div className="text-[#94A3B8]">{t('common.loading')}</div>;
  }

  const filteredAgents = sortData(agents.filter(a => {
    if (siteFilter !== 'all' && a.assigned_site !== siteFilter) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return a.name?.toLowerCase().includes(q) || a.assigned_site?.toLowerCase().includes(q) || a.phone?.toLowerCase().includes(q) || a.email?.toLowerCase().includes(q) || a.matricule?.toLowerCase().includes(q);
  }));

  return (
    <div className="space-y-6" data-testid="agents-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight">{t('agents.title')}</h1>
          <p className="text-[#94A3B8] mt-1 text-sm sm:text-base">Gestion des agents de sécurité</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Zone Filter */}
          {userPermissions?.can_view_all_zones && (
            <div className="flex items-center gap-2">
              <Funnel size={20} className="text-[#94A3B8]" />
              <div className="w-[180px]">
              <SearchableSelect value={selectedZone} onValueChange={setSelectedZone} options={[{ value: 'all', label: 'Toutes les zones' }, ...zones.map(z => ({ value: z.zone_id, label: z.name }))]} placeholder="Toutes les zones" testId="zone-filter" />
            </div>
            </div>
          )}

          <ExportButton endpoint="agents" filename="agents" label="Export Excel" />

          {/* Site Filter */}
          <div className="w-[200px]">
            <SearchableSelect value={siteFilter} onValueChange={setSiteFilter} options={[{ value: 'all', label: 'Tous les sites' }, ...[...new Set(agents.map(a => a.assigned_site).filter(Boolean))].sort().map(s => ({ value: s, label: s }))]} placeholder="Tous les sites" searchPlaceholder="Rechercher un site..." testId="site-filter" />
          </div>

          {canEdit && (
            <Dialog open={isDialogOpen} onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="add-agent-button">
                  <Plus size={20} className="mr-2" weight="bold" />
                  {t('agents.add')}
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold">
                    {editingAgent ? 'Modifier l\'agent' : t('agents.add')}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-5 max-h-[75vh] overflow-y-auto pr-1">
                  {/* Photo + Signature */}
                  <div className="flex justify-center items-end gap-8">
                    <div className="text-center">
                      <PhotoUpload currentUrl={formData.photo_url} onUploaded={(url) => setFormData({ ...formData, photo_url: url })} size="lg" />
                      <p className="text-[10px] text-[#64748B] mt-1">Photo</p>
                    </div>
                    <div className="text-center">
                      {formData.id_card_url && <img src={formData.id_card_url} alt="Pièce d'identité" className="max-h-16 rounded mb-1 mx-auto border border-[#334155]" />}
                      <PhotoUpload currentUrl={formData.id_card_url} onUploaded={(url) => setFormData({ ...formData, id_card_url: url })} size="sm" />
                      <p className="text-[10px] text-[#64748B] mt-1">Pièce d&apos;identité</p>
                    </div>
                    <div className="text-center">
                      {formData.signature_url && <img src={formData.signature_url} alt="Signature" className="max-h-14 bg-white rounded p-1 mb-1 mx-auto" />}
                      <PhotoUpload currentUrl={formData.signature_url} onUploaded={(url) => setFormData({ ...formData, signature_url: url })} size="sm" />
                      <p className="text-[10px] text-[#64748B] mt-1 flex items-center gap-1 justify-center"><Signature size={11} /> Signature</p>
                      <Button type="button" size="sm" variant="outline" onClick={() => setShowAgentSigPad(!showAgentSigPad)} className="mt-1 h-7 text-xs border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]" data-testid="agent-sign-onscreen-button">
                        ✍️ Signer à l&apos;écran
                      </Button>
                    </div>
                  </div>

                  {/* Fonction affichée sur la carte de service */}
                  <div>
                    <Label htmlFor="fonction" className="text-[#F8FAFC]">Fonction (imprimée sur la carte de service)</Label>
                    <Input id="fonction" value={formData.fonction} onChange={(e) => setFormData({ ...formData, fonction: e.target.value })} placeholder="Ex : Agent de sécurité, Chef de poste, Resp.CO..." className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="agent-fonction-input" />
                  </div>
                  {showAgentSigPad && (
                    <div className="border border-[#334155]/50 rounded-md p-4">
                      <p className="text-xs font-semibold text-[#2563EB] uppercase tracking-wider mb-2">Signature de l'agent</p>
                      <SignaturePad onSaved={(url) => { setFormData(f => ({ ...f, signature_url: url })); setShowAgentSigPad(false); toast.success('Signature de l\'agent enregistrée'); }} />
                    </div>
                  )}

                  {/* Identification */}
                  <div className="border border-[#334155]/50 rounded-md p-4 space-y-3">
                    <p className="text-xs font-semibold text-[#2563EB] uppercase tracking-wider">Identification</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div><Label className="text-[#F8FAFC] text-xs">Matricule Entreprise</Label><Input value={formData.matricule} onChange={e => setFormData({...formData, matricule: e.target.value})} placeholder="GS-001" className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="agent-matricule-input" /></div>
                      <div><Label className="text-[#F8FAFC] text-xs">N° CNSS</Label><Input value={formData.cnss_number} onChange={e => setFormData({...formData, cnss_number: e.target.value})} placeholder="CNSS-..." className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="agent-cnss-input" /></div>
                      <div><Label className="text-[#F8FAFC] text-xs">N° INPP</Label><Input value={formData.inpp_number} onChange={e => setFormData({...formData, inpp_number: e.target.value})} placeholder="INPP-..." className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="agent-inpp-input" /></div>
                    </div>
                  </div>

                  {/* État Civil */}
                  <div className="border border-[#334155]/50 rounded-md p-4 space-y-3">
                    <p className="text-xs font-semibold text-[#10B981] uppercase tracking-wider">État Civil</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div><Label className="text-[#F8FAFC] text-xs">Nom *</Label><Input value={formData.nom} onChange={e => setFormData({...formData, nom: e.target.value})} required placeholder="BOLIA" className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="agent-nom-input" /></div>
                      <div><Label className="text-[#F8FAFC] text-xs">Post-Nom</Label><Input value={formData.post_nom} onChange={e => setFormData({...formData, post_nom: e.target.value})} placeholder="MONGOMBA" className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="agent-postnom-input" /></div>
                      <div><Label className="text-[#F8FAFC] text-xs">Prénom *</Label><Input value={formData.prenom} onChange={e => setFormData({...formData, prenom: e.target.value})} required placeholder="Dplames" className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="agent-prenom-input" /></div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <Label className="text-[#F8FAFC] text-xs">État civil</Label>
                        <Select value={formData.etat_civil || 'none'} onValueChange={v => setFormData({...formData, etat_civil: v === 'none' ? '' : v})}>
                          <SelectTrigger className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]"><SelectValue placeholder="Choisir" /></SelectTrigger>
                          <SelectContent className="bg-[#0F172A] border-[#1E293B]">
                            <SelectItem value="none" className="text-[#F8FAFC]">Non précisé</SelectItem>
                            <SelectItem value="celibataire" className="text-[#F8FAFC]">Célibataire</SelectItem>
                            <SelectItem value="marie" className="text-[#F8FAFC]">Marié(e)</SelectItem>
                            <SelectItem value="divorce" className="text-[#F8FAFC]">Divorcé(e)</SelectItem>
                            <SelectItem value="veuf" className="text-[#F8FAFC]">Veuf/Veuve</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-[#F8FAFC] text-xs">Nombre d'enfants</Label><Input type="number" min="0" value={formData.nombre_enfants} onChange={e => setFormData({...formData, nombre_enfants: e.target.value})} placeholder="0" className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" /></div>
                      <div><Label className="text-[#F8FAFC] text-xs">Adresse</Label><Input value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} placeholder="Avenue, Commune, Ville" className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="agent-address-input" /></div>
                    </div>
                  </div>

                  {/* Contact */}
                  <div className="border border-[#334155]/50 rounded-md p-4 space-y-3">
                    <p className="text-xs font-semibold text-[#F59E0B] uppercase tracking-wider">Contact</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div><Label className="text-[#F8FAFC] text-xs">Téléphone *</Label><Input value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} required placeholder="+243 ..." className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="agent-phone-input" /></div>
                      <div><Label className="text-[#F8FAFC] text-xs">Email *</Label><Input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} required placeholder="agent@gazelle.com" className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="agent-email-input" /></div>
                    </div>
                  </div>

                  {/* Contrat & Rémunération */}
                  <div className="border border-[#334155]/50 rounded-md p-4 space-y-3">
                    <p className="text-xs font-semibold text-[#8B5CF6] uppercase tracking-wider">Contrat & Rémunération</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div><Label className="text-[#F8FAFC] text-xs">Date d'engagement</Label><Input type="date" value={formData.date_engagement} onChange={e => setFormData({...formData, date_engagement: e.target.value})} className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="agent-engagement-input" /></div>
                      <div><Label className="text-[#F8FAFC] text-xs">Rémunération (USD)</Label><Input type="number" min="0" step="0.01" value={formData.remuneration} onChange={e => setFormData({...formData, remuneration: e.target.value})} placeholder="0.00" className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="agent-remuneration-input" /></div>
                    </div>
                  </div>

                  {/* Affectation */}
                  <div className="border border-[#334155]/50 rounded-md p-4 space-y-3">
                    <p className="text-xs font-semibold text-[#22D3EE] uppercase tracking-wider">Affectation</p>
                    <div>
                      <Label className="text-[#F8FAFC] text-xs">Zone / Bureau</Label>
                      {showNewZoneInput ? (
                        <div className="flex gap-2 mt-1">
                          <Input value={newZoneName} onChange={e => setNewZoneName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addNewZone(); } }} placeholder="Nouvelle zone..." className="bg-[#1E293B] border-[#334155] text-[#F8FAFC] flex-1" autoFocus data-testid="new-zone-input" />
                          <Button type="button" onClick={addNewZone} className="bg-[#10B981] hover:bg-[#059669] text-white px-3"><Plus size={16} /></Button>
                          <Button type="button" variant="ghost" onClick={() => setShowNewZoneInput(false)} className="text-[#94A3B8] hover:text-white px-2"><X size={16} /></Button>
                        </div>
                      ) : (
                        <div className="flex gap-2 mt-1">
                          <Select value={formData.zone_id} onValueChange={v => setFormData({...formData, zone_id: v})}><SelectTrigger className="bg-[#1E293B] border-[#334155] text-[#F8FAFC] flex-1" data-testid="zone-select"><SelectValue placeholder="Sélectionner" /></SelectTrigger><SelectContent className="bg-[#0F172A] border-[#1E293B]">{zones.map(z => <SelectItem key={z.zone_id} value={z.zone_id} className="text-[#F8FAFC]">{z.name}</SelectItem>)}</SelectContent></Select>
                          <Button type="button" onClick={() => setShowNewZoneInput(true)} className="bg-[#2563EB]/20 hover:bg-[#2563EB]/40 text-[#2563EB] border border-[#2563EB]/30 px-3" data-testid="add-zone-button"><Plus size={16} /></Button>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-[#F8FAFC] text-xs">Client affecté</Label>
                        <Select value={formData.client_id} onValueChange={v => setFormData({...formData, client_id: v, assigned_site: 'none'})}><SelectTrigger className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]"><SelectValue placeholder="Sélectionner" /></SelectTrigger><SelectContent className="bg-[#0F172A] border-[#1E293B]"><SelectItem value="none" className="text-[#F8FAFC]">Non affecté</SelectItem>{clients.map(c => <SelectItem key={c.client_id} value={c.client_id} className="text-[#F8FAFC]">{c.name}</SelectItem>)}</SelectContent></Select>
                      </div>
                      <div>
                        <Label className="text-[#F8FAFC] text-xs">Site affecté</Label>
                        {(() => { const sites = clients.find(c => c.client_id === formData.client_id)?.sites || []; return (<Select value={formData.assigned_site} onValueChange={v => setFormData({...formData, assigned_site: v})}><SelectTrigger className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]"><SelectValue placeholder="Choisir" /></SelectTrigger><SelectContent className="bg-[#0F172A] border-[#1E293B]"><SelectItem value="none" className="text-[#F8FAFC]">Non affecté</SelectItem>{sites.map(s => <SelectItem key={s} value={s} className="text-[#F8FAFC]">{s}</SelectItem>)}</SelectContent></Select>); })()}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-[#F8FAFC] text-xs">{t('agents.availability')}</Label>
                        <Select value={formData.availability} onValueChange={v => setFormData({...formData, availability: v})}><SelectTrigger className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]"><SelectValue /></SelectTrigger><SelectContent className="bg-[#0F172A] border-[#1E293B]"><SelectItem value="available" className="text-[#F8FAFC]">{t('agents.available')}</SelectItem><SelectItem value="assigned" className="text-[#F8FAFC]">{t('agents.assigned')}</SelectItem><SelectItem value="unavailable" className="text-[#F8FAFC]">{t('agents.unavailable')}</SelectItem></SelectContent></Select>
                      </div>
                      <div><Label className="text-[#F8FAFC] text-xs">{t('agents.skills')} (virgule)</Label><Input value={formData.skills} onChange={e => setFormData({...formData, skills: e.target.value})} placeholder="Surveillance, Intervention..." className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" /></div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]">{t('common.cancel')}</Button>
                    <Button type="submit" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="save-agent-button">{t('common.save')}</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
        <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Rechercher par nom, site ou téléphone..." className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] pl-9" data-testid="search-agent" />
      </div>

      <div className="bg-[#0F172A] border border-[#1E293B] rounded-md overflow-hidden">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#1E293B]/50">
              <tr>
                <SortableHeader label="Matricule" sortKey="matricule" sortConfig={sortConfig} onSort={onSort} />
                <SortableHeader label={t('agents.name')} sortKey="name" sortConfig={sortConfig} onSort={onSort} />
                <SortableHeader label="Adresse" sortKey="address" sortConfig={sortConfig} onSort={onSort} />
                <SortableHeader label="Site affecté" sortKey="assigned_site" sortConfig={sortConfig} onSort={onSort} />
                <SortableHeader label="Zone" sortKey="zone_id" sortConfig={sortConfig} onSort={onSort} />
                <SortableHeader label={t('agents.phone')} sortKey="phone" sortConfig={sortConfig} onSort={onSort} />
                <SortableHeader label={t('agents.availability')} sortKey="availability" sortConfig={sortConfig} onSort={onSort} />
                {canEdit && (
                  <th className="px-6 py-4 text-right text-sm font-medium text-[#F8FAFC] uppercase tracking-wider">{t('common.actions')}</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1E293B]">
              {filteredAgents.length === 0 ? (
                <tr>
                  <td colSpan={canEdit ? 8 : 7} className="px-6 py-8 text-center text-[#94A3B8]">Aucun agent trouvé</td>
                </tr>
              ) : (
                filteredAgents.map((agent) => (
                  <tr key={agent.agent_id} className="hover:bg-[#1E293B]/30 transition-colors" data-testid={`agent-row-${agent.agent_id}`}>
                    <td className="px-6 py-4 text-[#F8FAFC] font-mono text-sm">{agent.matricule || '-'}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {agent.photo_url ? (
                          <img
                            src={agent.photo_url}
                            alt={agent.name}
                            onClick={(e) => { e.stopPropagation(); setLightbox({ src: agent.photo_url, alt: agent.name, filename: `${agent.matricule || agent.name}_photo.jpg` }); }}
                            className="h-9 w-9 rounded-full object-cover border border-[#334155] cursor-zoom-in hover:ring-2 hover:ring-[#2563EB] transition-all"
                            data-testid={`agent-photo-${agent.agent_id}`}
                          />
                        ) : (
                          <div className="h-9 w-9 rounded-full bg-[#2563EB] flex items-center justify-center text-white text-sm font-medium border border-[#334155]">
                            {agent.name?.charAt(0) || 'A'}
                          </div>
                        )}
                        <div>
                          <div className="text-[#F8FAFC] font-medium">{agent.name}</div>
                          <div className="text-sm text-[#94A3B8]">{agent.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-[#94A3B8]">{agent.address || '-'}</td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-[#F8FAFC]">{agent.assigned_site || '-'}</div>
                        {agent.assignment_zone && (
                          <div className="flex items-center gap-1 text-sm text-[#94A3B8]">
                            <MapPin size={14} />
                            {agent.assignment_zone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <Badge className="bg-[#2563EB]/20 text-[#2563EB] border border-[#2563EB]/30">
                        {getZoneName(agent.zone_id)}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-[#94A3B8]">
                      <div className="flex items-center gap-2">
                        <span>{agent.phone || '-'}</span>
                        <ContactActions phone={agent.phone} name={agent.name} size="xs" />
                      </div>
                    </td>
                    <td className="px-6 py-4">{getAvailabilityBadge(agent.availability)}</td>
                    {canEdit && (
                      <td className="px-6 py-4 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => { setTransferAgent(agent); setTransferSite(''); }}
                          className="text-[#8B5CF6] hover:text-white hover:bg-[#8B5CF6]"
                          title="Transférer vers un autre site"
                          data-testid={`transfer-agent-${agent.agent_id}`}
                        >
                          <ArrowsLeftRight size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setDossierAgent(agent)}
                          className="text-[#F59E0B] hover:text-white hover:bg-[#F59E0B]"
                          title="Dossier de l'agent"
                          data-testid={`dossier-agent-${agent.agent_id}`}
                        >
                          <FolderOpen size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(agent)}
                          className="text-[#2563EB] hover:text-white hover:bg-[#2563EB]"
                          data-testid={`edit-agent-${agent.agent_id}`}
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/agents/${agent.agent_id}/pdf`, '_blank')}
                          className="text-[#10B981] hover:text-white hover:bg-[#10B981]"
                          title="Télécharger la fiche PDF"
                        >
                          <FilePdf size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/agents/${agent.agent_id}/service-card/pdf`, '_blank')}
                          className="text-[#F59E0B] hover:text-white hover:bg-[#F59E0B]"
                          title="Carte de service (avec QR)"
                          data-testid={`service-card-${agent.agent_id}`}
                        >
                          <IdentificationCard size={16} />
                        </Button>
                        {userRole === 'admin' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(agent.agent_id)}
                            className="text-[#EF4444] hover:text-white hover:bg-[#EF4444]"
                            data-testid={`delete-agent-${agent.agent_id}`}
                            title="Supprimer l'agent"
                          >
                            <Trash size={16} />
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden divide-y divide-[#1E293B]">
          {filteredAgents.length === 0 ? (
            <div className="px-4 py-8 text-center text-[#94A3B8]">Aucun agent trouvé</div>
          ) : filteredAgents.map((agent) => (
            <div key={agent.agent_id} className="p-4 space-y-3" data-testid={`agent-card-${agent.agent_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0">
                  {agent.photo_url ? (
                    <img
                      src={agent.photo_url}
                      alt={agent.name}
                      onClick={(e) => { e.stopPropagation(); setLightbox({ src: agent.photo_url, alt: agent.name, filename: `${agent.matricule || agent.name}_photo.jpg` }); }}
                      className="h-10 w-10 rounded-full object-cover border border-[#334155] flex-shrink-0 cursor-zoom-in hover:ring-2 hover:ring-[#2563EB] transition-all"
                      data-testid={`agent-photo-mobile-${agent.agent_id}`}
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-[#2563EB] flex items-center justify-center text-white text-sm font-medium flex-shrink-0">{agent.name?.charAt(0) || 'A'}</div>
                  )}
                  <div className="min-w-0">
                    <p className="text-[#F8FAFC] font-medium truncate">{agent.name}</p>
                    <p className="text-xs text-[#94A3B8] font-mono">{agent.matricule || '-'}</p>
                  </div>
                </div>
                {canEdit && (
                  <div className="flex flex-shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => setDossierAgent(agent)} className="text-[#F59E0B] hover:text-white hover:bg-[#F59E0B] h-8 w-8 p-0"><FolderOpen size={15} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => handleEdit(agent)} className="text-[#2563EB] hover:text-white hover:bg-[#2563EB] h-8 w-8 p-0"><Pencil size={15} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/agents/${agent.agent_id}/pdf`, '_blank')} className="text-[#10B981] hover:text-white hover:bg-[#10B981] h-8 w-8 p-0"><FilePdf size={15} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/agents/${agent.agent_id}/service-card/pdf`, '_blank')} className="text-[#F59E0B] hover:text-white hover:bg-[#F59E0B] h-8 w-8 p-0" title="Carte de service"><IdentificationCard size={15} /></Button>
                    {userRole === 'admin' && (
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(agent.agent_id)} className="text-[#EF4444] hover:text-white hover:bg-[#EF4444] h-8 w-8 p-0"><Trash size={15} /></Button>
                    )}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#64748B]">{t('agents.phone')}</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-[#94A3B8]">{agent.phone || '-'}</p>
                    <ContactActions phone={agent.phone} name={agent.name} size="xs" />
                  </div>
                </div>
                <div><p className="text-[10px] uppercase tracking-wider text-[#64748B]">Site affecté</p><p className="text-[#94A3B8] truncate">{agent.assigned_site || '-'}</p></div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge className="bg-[#2563EB]/20 text-[#2563EB] border border-[#2563EB]/30 text-xs">{getZoneName(agent.zone_id)}</Badge>
                {getAvailabilityBadge(agent.availability)}
              </div>
            </div>
          ))}
        </div>
      </div>

      <AgentDossier agent={dossierAgent} open={!!dossierAgent} onOpenChange={(o) => !o && setDossierAgent(null)} userRole={userRole} />

      {/* Dialog Transfert de site */}
      <Dialog open={!!transferAgent} onOpenChange={(o) => !o && setTransferAgent(null)}>
        <DialogContent className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <ArrowsLeftRight size={20} className="text-[#8B5CF6]" /> Transférer {transferAgent?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-[#1E293B]/40 rounded-md border border-[#334155]/50">
              <p className="text-[10px] uppercase tracking-wider text-[#64748B]">Site actuel</p>
              <p className="text-sm text-[#F8FAFC] font-medium">{transferAgent?.assigned_site || 'Aucun site'}</p>
            </div>
            <div>
              <Label className="text-[#F8FAFC]">Nouveau site *</Label>
              <div className="mt-1">
                <SearchableSelect value={transferSite} onValueChange={setTransferSite} options={[...new Set([...clientsList.flatMap(c => c.sites || []), ...agents.map(a => a.assigned_site).filter(Boolean)])].sort().map(s => ({ value: s, label: s }))} placeholder="Choisir le nouveau site" searchPlaceholder="Rechercher un site..." testId="transfer-site-select" />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setTransferAgent(null)} className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]">Annuler</Button>
              <Button onClick={handleTransfer} className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white" data-testid="confirm-transfer-button">
                <ArrowsLeftRight size={15} className="mr-2" /> Transférer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lightbox visionneuse plein écran */}
      <ImageLightbox
        open={!!lightbox}
        src={lightbox?.src}
        alt={lightbox?.alt}
        filename={lightbox?.filename}
        onClose={() => setLightbox(null)}
      />
    </div>
  );
};

export default Agents;
